/**
 * Stars on every hub, and where a starred screen opens (HOME-005).
 *
 * The Transactions hub had stars; Masters, Reports and Dashboards did not, so
 * the reference's home page — which lists the Items master beside Purchase
 * Orders — could not be set up. One toggle, keyed the way the server stores
 * it: a transaction type as itself, anything else with its hub as a prefix
 * (`master:item`, `report:cost_variance`, `dashboard:project_overview`,
 * `page:drive`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { IconName } from '../design/index.js'
import { useApi, type Favourite } from '../app/api.js'
import { paths } from '../app/router.js'

/** The keys this person has starred, shared by every hub. */
export const useFavouriteKeys = () => {
  const api = useApi()
  return useQuery({ queryKey: ['favourites', api.companyId], queryFn: () => api.favourites(), staleTime: 30_000 })
}

/** Everything that shows a favourite is refreshed when one changes. */
export const useFavouriteMutations = () => {
  const api = useApi()
  const client = useQueryClient()
  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['favourites', api.companyId] })
    void client.invalidateQueries({ queryKey: ['menu', api.companyId] })
    void client.invalidateQueries({ queryKey: ['home', api.companyId] })
  }
  const toggle = useMutation({
    mutationFn: ({ key, on }: { key: string; on: boolean }) => api.setFavourite(key, on),
    onSuccess: refresh,
  })
  const move = useMutation({
    mutationFn: ({ key, direction }: { key: string; direction: 'up' | 'down' }) => api.moveFavourite(key, direction),
    onSuccess: refresh,
  })
  return { toggle, move }
}

/** The ☆ / ★ on a hub tile. */
export const StarToggle = ({ menuKey, label }: { menuKey: string; label: string }) => {
  const keys = useFavouriteKeys()
  const { toggle } = useFavouriteMutations()
  const on = (keys.data ?? []).includes(menuKey)
  return (
    <button
      type="button"
      className={on ? 'star-toggle star-toggle--on' : 'star-toggle'}
      aria-pressed={on}
      aria-label={on ? `Remove ${label} from favourites` : `Add ${label} to favourites`}
      disabled={toggle.isPending || keys.isLoading}
      onClick={() => toggle.mutate({ key: menuKey, on: !on })}
    >
      {on ? '★' : '☆'}
    </button>
  )
}

/** Where a starred screen opens. */
export const favouritePath = (companyId: string, f: Pick<Favourite, 'kind' | 'route'>): string => {
  switch (f.kind) {
    case 'document': return paths.list(companyId, f.route)
    case 'master': return paths.masterList(companyId, f.route)
    case 'report': return paths.report(companyId, f.route)
    case 'dashboard': return paths.dashboard(companyId, f.route)
    case 'page': return f.route === 'drive' ? paths.drive(companyId) : paths.home(companyId)
  }
}

/** The icon a shortcut carries for the hub it came from. */
export const HUB_ICON: Readonly<Record<string, IconName>> = {
  Transactions: 'doc',
  Masters: 'box',
  Reports: 'chart',
  Dashboards: 'grid',
}
